import React, { useCallback, useEffect, useState } from 'react';
import { Select, Table, InputNumber, Button, Space, Tag, notification } from 'antd';
import { PREMIUM_TYPE_LABEL, subBandSubtitle, pricingModeSummary } from '../../components/PlanManagement/premiumTypes';
import lib from '../../assets/utils/lib';
import pmApi from './pmApi';

/**
 * Org Plan Management — band-level price overrides (stakeholder change #3/#4).
 *
 * The bands an organization has SET UP here are exactly the bands offered
 * when adding a client to that organization (org-scoped list), and the
 * override price is what gets charged — and therefore what lands on the
 * pricing event. Bands without a row are hidden from that org's client
 * addition once at least one band is configured.
 */

const fmtMoney = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

export default function OrgBandOverrides({ planId, plan, onChanged }) {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [data, setData] = useState(null);
  const [allPlanBands, setAllPlanBands] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingBand, setSavingBand] = useState(null);

  useEffect(() => {
    // Organizations list via the existing corporations API (any shape).
    (async () => {
      try {
        const resp = await lib.getOrganizationsV2({ per_page: 500 });
        const payload = resp?.data?.data;
        const rows = payload?.organizations || payload?.items || payload || [];
        const mapped = (Array.isArray(rows) ? rows : []).map((o) => ({
          value: o.org_id ?? o.id,
          label: o.org_name ?? o.name,
        })).filter((o) => o.value);
        setOrgs(mapped);
      } catch (e) {
        setOrgs([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!orgId || !planId) { setData(null); return; }
    const [eff, pb] = await Promise.all([pmApi.orgBands(orgId, planId), pmApi.planBands(planId)]);
    if (eff.ok) setData(eff.data);
    if (pb.ok) setAllPlanBands(pb.data.bands || []);
    setDrafts({});
  }, [orgId, planId]);

  useEffect(() => { load(); }, [load]);

  const save = async (bandId, price) => {
    setSavingBand(bandId);
    const res = await pmApi.setOrgPrice(planId, orgId, bandId, { price });
    setSavingBand(null);
    if (res.ok) {
      notification.success({ message: price ? 'Override set' : 'Override cleared' });
      load(); onChanged?.();
    }
  };

  const effectiveByBand = {};
  (data?.bands || []).forEach((b) => { effectiveByBand[b.band_id] = b; });

  return (
    <div style={{ marginTop: 22 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--pm-accent-ink)' }}>
        Org plan management — band overrides {plan ? `on ${plan.plan_name}` : ''}
      </h4>
      <Space style={{ marginBottom: 10 }} wrap>
        <Select
          showSearch
          style={{ minWidth: 260 }}
          placeholder="Pick an organization…"
          value={orgId}
          onChange={setOrgId}
          optionFilterProp="label"
          options={orgs}
          notFoundContent="No organizations loaded"
        />
        {data ? (
          data.org_scoped
            ? <Tag color="green" style={{ marginInlineEnd: 0 }}>org-scoped: {data.bands.length} band(s) offered to clients</Tag>
            : <Tag color="gold" style={{ marginInlineEnd: 0 }}>no bands set up — client addition falls back to all plan bands</Tag>
        ) : null}
      </Space>

      {orgId && data ? (
        <Table
          size="small"
          rowKey={(r) => r.band_id}
          pagination={false}
          dataSource={allPlanBands.filter((b) => Number(b.status) === 1)}
          columns={[
            {
              title: 'Band',
              key: 'band',
              render: (_, b) => (
                <div>
                  <b>{b.band_name}</b>{' '}
                  <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>{PREMIUM_TYPE_LABEL[b.premium_type] || b.premium_type || 'Family'}</Tag>
                  <div className="pm-benefit-sub">{subBandSubtitle({ ...b, name: b.band_name }) + (b.premium_type === 'dependent' && b.pricing_mode && b.pricing_mode !== 'fixed' ? ` · ${pricingModeSummary(b)}` : '')}</div>
                </div>
              ),
            },
            {
              title: 'Standard price',
              key: 'std',
              render: (_, b) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(b.current_price)}</span>,
            },
            {
              title: 'Org override',
              key: 'ov',
              render: (_, b) => {
                const eff = effectiveByBand[b.band_id];
                const current = eff?.override_price ?? null;
                return (
                  <Space.Compact>
                    <InputNumber
                      size="small"
                      style={{ width: 140 }}
                      min={0}
                      placeholder={current !== null ? undefined : 'no override'}
                      value={drafts[b.band_id] !== undefined ? drafts[b.band_id] : current}
                      onChange={(v) => setDrafts((d) => ({ ...d, [b.band_id]: v }))}
                      formatter={(v) => (v === null || v === undefined || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                      parser={(v) => `${v}`.replace(/,/g, '')}
                    />
                    <Button
                      size="small"
                      type="primary"
                      loading={savingBand === b.band_id}
                      disabled={drafts[b.band_id] === undefined}
                      onClick={() => save(b.band_id, drafts[b.band_id] || 0)}
                    >
                      {drafts[b.band_id] ? 'Set' : 'Clear'}
                    </Button>
                  </Space.Compact>
                );
              },
            },
            {
              title: 'Offered to clients',
              key: 'eff',
              render: (_, b) => {
                const eff = effectiveByBand[b.band_id];
                if (!eff) return <span className="pm-chip pm-chip-muted">hidden</span>;
                return (
                  <Space size={6}>
                    <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(eff.effective_price)}</b>
                    {eff.override_price !== null ? <span className="pm-chip pm-chip-blue">override</span> : <span className="pm-chip pm-chip-muted">standard</span>}
                  </Space>
                );
              },
            },
          ]}
        />
      ) : null}
      {orgId && data ? (
        <div className="pm-benefit-sub" style={{ marginTop: 6 }}>
          The override price is what this organization&apos;s clients are charged — it lands on the
          pricing event at purchase/renewal, so revenue and MLR need no special handling.
        </div>
      ) : null}
    </div>
  );
}
